<template>
  <div class="betascore-tracker-widget-page">
    <BetaScoreTrackerWidget
      :loading="tracker.loading.value"
      :items="paginatedItems"
      :sort="tracker.sort.value"
      :has-you-items="tracker.hasYouItems.value"
      :has-organization-items="tracker.hasOrganizationItems.value"
      :has-history-items="tracker.hasHistoryItems.value"
      :pagination="tracker.pagination.value"
      @toggle-sort="tracker.toggleSort"
      @change-page="tracker.goToPage"
    />
  </div>
</template>

<script>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useScoreUsageTracker } from '@/composables/useScoreUsageTracker'
import BetaScoreTrackerWidget from '@/components/widget/betascoreTracker/index.vue'
import { TrackerSort } from '@/enums'

function buildFakeItems(count, isHistory) {
  const tiers = ['Prime Tier', 'Growth Tier', 'Emerging Tier', 'Monitor Tier']
  const confidences = ['High', 'Medium', 'Low']
  const buckets = [5, 4, 3, 2]

  return Array.from({ length: count }, (_, i) => {
    const expiresAt = isHistory
      ? new Date(Date.now() - (i + 1) * 86400000)
      : new Date(Date.now() + (i + 1) * 86400000)

    return {
      businessId: i + 1,
      name: `Fake Business ${i + 1}`,
      address: '123 QA Street',

      viewedAt: new Date(Date.now() - i * 86400000),
      expiresAt,
      daysRemaining: isHistory ? 0 : i + 1,

      tier: tiers[i % tiers.length],
      riskLevel: 'Medium',
      bucket: buckets[i % buckets.length],
      confidence: confidences[i % confidences.length],

      hasScore: true
    }
  })
}

export default {
  name: 'BetaScoreTrackerWidgetPage',
  components: { BetaScoreTrackerWidget },

  setup() {
    const route = useRoute()

    const youCount = Number(route.query.you ?? 0)
    const orgCount = Number(route.query.org ?? 0)
    const historyCount = Number(route.query.history ?? 0)

    const isHistory = computed(() => route.hash === '#history')

    const tracker = useScoreUsageTracker({
      initialSort: route.query.sort || TrackerSort.RECENT,
      mockYouItems: buildFakeItems(youCount, false),
      mockOrgItems: buildFakeItems(orgCount, false),
      mockHistoryItems: buildFakeItems(historyCount, true)
    })

    tracker.preloadScopes()

    const sortedItems = computed(() => {
      const items = [...tracker.items.value]

      if (isHistory.value) {
        return items.sort(
          (a, b) => new Date(b.viewedAt) - new Date(a.viewedAt)
        )
      }

      const sortByOldest = tracker.sort.value === TrackerSort.OLDEST

      return items.sort((a, b) => {
        const expA = new Date(a.expiresAt).getTime()
        const expB = new Date(b.expiresAt).getTime()

        if (expA !== expB) {
          return sortByOldest ? expB - expA : expA - expB
        }

        return new Date(b.viewedAt).getTime() - new Date(a.viewedAt).getTime()
      })
    })

    const paginatedItems = computed(() => {
      const { page, limit } = tracker.pagination.value
      const start = (page - 1) * limit
      const end = start + limit
      return sortedItems.value.slice(start, end)
    })

    return {
      tracker,
      paginatedItems
    }
  }
}
</script>

<style scoped lang="scss">
.betascore-tracker-widget-page {
  @include stack-space($space-large);
  @include inset-space($space-large);
}
</style>
